import React, { useEffect, useState, useCallback } from "react";
import Marquee from "react-fast-marquee";
import Styles from "./DamStrip.module.css";
import { getDamData } from "../../../../Services/BuySell/Listing";
import { formatDateType } from "../../../../commons/FormatDate";

// A day is divided into 96 fifteen-minute slots; slot 1 = 00:00-00:15.
// Returns the time range for a given time_slot_id, e.g. 43 -> "10:30 - 10:45".
const formatSlotRange = (slotId) => {
  if (slotId == null) return "";
  const startMin = (slotId - 1) * 15;
  const endMin = slotId * 15;
  const toHHMM = (mins) => {
    const h = String(Math.floor(mins / 60) % 24).padStart(2, "0");
    const m = String(mins % 60).padStart(2, "0");
    return `${h}:${m}`;
  };
  return `${toHHMM(startMin)} - ${toHHMM(endMin)}`;
};

const DamStrip = ({ speed = 25, pauseOnHover = true }) => {
  const [damData, setDamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchDamData = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) {
        setLoading(true);
      }

      const data = await getDamData();
      setDamData(data);
      setError(false);
    } catch (err) {
      // Only surface the error on the initial load. On a silent background
      // refresh, keep showing the last-good data instead of blanking the strip.
      if (showLoader) {
        setError(true);
      }
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    // Initial load with skeleton
    fetchDamData(true);

    let timeoutId;
    let intervalId;

    // Align the refresh to real wall-clock quarter-hours (:00, :15, :30, :45)
    // instead of "15 min after mount". So if the page opens at 16:22 the next
    // refresh happens at 16:30, then 16:45, 17:00 ... with no action needed.
    const QUARTER = 15 * 60 * 1000;
    const now = Date.now();
    const msToNextQuarter = QUARTER - (now % QUARTER);

    timeoutId = setTimeout(() => {
      fetchDamData(false);
      // From the boundary onward, tick every 15 minutes.
      intervalId = setInterval(() => {
        fetchDamData(false);
      }, QUARTER);
    }, msToNextQuarter);

    // Background timers get throttled/paused when the tab is hidden or the
    // machine sleeps, so a scheduled tick can be skipped -- that's why a hard
    // refresh was needed. Refresh as soon as the tab becomes visible again so
    // the strip is up to date without any manual reload.
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchDamData(false);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleVisibility);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleVisibility);
    };
  }, [fetchDamData]);

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ display: "flex", gap: "10px" }}>
          <div className={Styles.stripBox}>
            <div className={Styles.skeletonText}></div>
            <div className={Styles.skeletonValue}></div>
          </div>
          <div className={Styles.stripBox}>
            <div className={Styles.skeletonText}></div>
            <div className={Styles.skeletonValue}></div>
          </div>
          <div className={Styles.stripBox}>
            <div className={Styles.skeletonText}></div>
            <div className={Styles.skeletonValue}></div>
          </div>
          <div className={Styles.stripBox}>
            <div className={Styles.skeletonText}></div>
            <div className={Styles.skeletonValue}></div>
          </div>
          <div className={Styles.stripBox}>
            <div className={Styles.skeletonText}></div>
            <div className={Styles.skeletonValue}></div>
          </div>
          <div className={Styles.stripBox}>
            <div className={Styles.skeletonText}></div>
            <div className={Styles.skeletonValue}></div>
          </div>
          <div className={Styles.stripBox}>
            <div className={Styles.skeletonText}></div>
            <div className={Styles.skeletonValue}></div>
          </div>
          <div className={Styles.stripBox}>
            <div className={Styles.skeletonText}></div>
            <div className={Styles.skeletonValue}></div>
          </div>
          <div className={Styles.stripBox}>
            <div className={Styles.skeletonText}></div>
            <div className={Styles.skeletonValue}></div>
          </div>
          <div className={Styles.stripBox}>
            <div className={Styles.skeletonText}></div>
            <div className={Styles.skeletonValue}></div>
          </div>
          <div className={Styles.stripBox}>
            <div className={Styles.skeletonText}></div>
            <div className={Styles.skeletonValue}></div>
          </div>
          <div className={Styles.stripBox}>
            <div className={Styles.skeletonText}></div>
            <div className={Styles.skeletonValue}></div>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className={Styles.stripBox}>
          <div className={Styles.error}>
            <span>⚠️</span> Failed to load DAM data
          </div>
        </div>
      );
    }

    const items = [
      {
        label: "DAM ",
        date: damData?.dam?.current_day?.date,
        val: damData?.dam?.current_day?.val,
      },
      {
        label: "DAM ",
        date: damData?.dam?.prev_day?.date,
        val: damData?.dam?.prev_day?.val,
      },
      {
        label: "RTM ",
        date: damData?.rtm?.current_day?.date,
        val: damData?.rtm?.current_day?.val,
        slotId: damData?.rtm?.current_day?.time_slot_id,
      },
    ];

    return (
      <div style={{ display: "flex" }}>
        {items.map((item, idx) => (
          <div
            key={idx}
            style={{ margin: "0px 10px" }}
            className={Styles.stripBox}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span
                className={
                  item.label.includes("DAM") ? Styles.damLabel : Styles.rtmLabel
                }
              >
                {item.label}
              </span>
              {item.date && (
                <span
                  style={{
                    color: item.label.includes("DAM") ? "#34656d" : "#d32f2f",
                    fontSize: "12px",
                  }}
                >
                  ({formatDateType(item.date)})
                </span>
              )}
              {item.slotId != null && (
                <span
                  style={{
                    color: item.label.includes("DAM") ? "#34656d" : "#d32f2f",
                    fontSize: "12px",
                  }}
                >
                  [Slot {item.slotId} ({formatSlotRange(item.slotId)})]
                </span>
              )}{" "}
              -
            </div>
            <div
              style={{
                color: item.label.includes("DAM") ? "#34656d" : "#d32f2f",
              }}
            >
              {item.val != null ? `${Number(item.val).toFixed(3)} ₹/kWh` : "-"}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={Styles.scheduleStrip}>
      <Marquee
        pauseOnHover={pauseOnHover}
        gradient={false}
        speed={speed}
        autoFill={!loading && !error}
        play={!loading}
      >
        {renderContent()}
      </Marquee>
    </div>
  );
};

export default DamStrip;
